import { Navigate, Route, Routes } from 'react-router-dom';
import { useLocation, useParams } from 'react-router';
import { CataloguePage } from './CataloguePage';
import { GenreEntryPage } from '../entrypages/genre/GenreEntryPage';
import { PublisherEntryPage } from '../entrypages/publishers/PublisherEntryPage';
import { NewReleasesEntryPage } from '../entrypages/newReleases/NewReleasesEntryPage';
import { FeaturedEntryPage } from '../entrypages/featured/FeaturedEntryPage';
import { PageNotFound } from '../PageNotFound';
import { BundlesOverviewPage } from '../../shared/bundles/BundlesOverviewPage';
import { ProductSelectionProvider } from '../../shared/product-selection/ProductSelectionContext';
import { ProductTypePage } from '../product-type/ProductTypePage';
import { CheckLoanFormatAndProductType } from '../product-type/CheckLoanFormatAndProductType';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../shared/utils/togglz';
import React, { FC } from 'react';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';
import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../../shared/loanFormat/constants';
import { log } from '../../shared/utils/log';
import { LanguageEntryPage } from '../entrypages/language/LanguageEntryPage';
import { CheckLoanFormat } from '../../shared/components/CheckLoanFormat';
import { LanguageProductsPage } from '../language/languages/LanguagesProductsPage';

export const CatalogueTemplate: FC = () => {
    const { pathname, search } = useLocation();
    const { loanFormat, shopLanguage } = useParams<'loanFormat' | 'shopLanguage'>();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();
    const searchParams = new URLSearchParams(ageCategorySearchParam ? { ageCategory: ageCategorySearchParam } : {});

    if (
        loanFormat !== LOANFORMAT_EAUDIOBOOK_OBJECT.urlKey &&
        loanFormat !== LOANFORMAT_EBOOK_OBJECT.urlKey &&
        loanFormat !== LOANFORMAT_EPRESS_OBJECT.urlKey
    ) {
        log.warning(`failed to parse valid loan format from url "${pathname}"`);
        return <Navigate to="/" replace />;
    }

    if (isMultiLanguageShopEnabled && !shopLanguage) {
        const redirectPath = pathname.replace('/catalogue', `/shop/en`);

        return <Navigate to={{ pathname: redirectPath, search }} replace />;
    }

    return (
        <Routes>
            <Route
                index
                element={<Navigate to={{ pathname: 'featured', search: searchParams.toString() }} replace />}
            />
            <Route path="featured" element={<FeaturedEntryPage />} />
            <Route path="languages">
                <Route
                    index
                    element={
                        shopLanguage === 'other' || shopLanguage === 'all' ? (
                            <LanguageEntryPage />
                        ) : (
                            <Navigate to={{ pathname: '..', search: searchParams.toString() }} replace />
                        )
                    }
                />
                <Route
                    path=":languageId/products"
                    element={
                        shopLanguage === 'other' || shopLanguage === 'all' ? (
                            <CheckLoanFormat>
                                <ProductSelectionProvider>
                                    <LanguageProductsPage />
                                </ProductSelectionProvider>
                            </CheckLoanFormat>
                        ) : (
                            <Navigate to={{ pathname: '..', search: searchParams.toString() }} replace />
                        )
                    }
                />
            </Route>
            <Route path="bundles" element={<BundlesOverviewPage />} />
            <Route
                path="newreleases"
                element={
                    loanFormat === LOANFORMAT_EPRESS_OBJECT.urlKey ? (
                        <Navigate to="featured" replace />
                    ) : (
                        <NewReleasesEntryPage />
                    )
                }
            />
            <Route
                path="products"
                element={
                    <ProductSelectionProvider>
                        <CataloguePage />
                    </ProductSelectionProvider>
                }
            />
            <Route path="genres/:genre?/:subGenre?" element={<GenreEntryPage />} />
            <Route path="publishers/:item?/:subItem?" element={<PublisherEntryPage />} />
            <Route
                path=":productType"
                element={
                    <CheckLoanFormatAndProductType>
                        {({ loanFormat, productType }) => (
                            <ProductSelectionProvider>
                                <ProductTypePage loanFormat={loanFormat} productType={productType} />
                            </ProductSelectionProvider>
                        )}
                    </CheckLoanFormatAndProductType>
                }
            />
            <Route path="*" element={<PageNotFound />} />
        </Routes>
    );
};
